import React from 'react';
import { Check, AlertCircle } from 'lucide-react';

export interface WizardStepInfo {
  name: string;
  completed: boolean;
  hasErrors: boolean;
  skipped?: boolean;
}

interface WizardStepIndicatorProps {
  steps: WizardStepInfo[];
  currentStep: number;
  visitedSteps: boolean[];
  onStepClick: (stepIndex: number) => void;
}

const WizardStepIndicator: React.FC<Readonly<WizardStepIndicatorProps>> = ({
  steps,
  currentStep,
  visitedSteps,
  onStepClick,
}) => {
  const handleStepClick = (index: number) => {
    // Can only click on visited steps
    if (visitedSteps[index]) {
      onStepClick(index);
    }
  };

  return (
    <div className="wizard-steps">
      {steps.map((step, index) => {
        const isCurrent = index === currentStep;
        const isVisited = visitedSteps[index];
        const isSkipped = step.skipped || false;
        const isDisabled = (!isVisited && !isCurrent) || isSkipped;

        // Calculate step number class
        let numberClass = 'wizard-step-number';
        if (isCurrent) numberClass += ' current';
        if (step.completed) numberClass += ' completed';
        if (step.hasErrors) numberClass += ' has-errors';
        if (isSkipped) numberClass += ' skipped';

        return (
          <React.Fragment key={index}>
            <div
              className={`wizard-step ${isCurrent ? 'current' : ''} ${isDisabled ? 'disabled' : ''} ${isSkipped ? 'skipped' : ''}`}
              onClick={() => !isSkipped && handleStepClick(index)}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              onKeyPress={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !isSkipped) {
                  handleStepClick(index);
                }
              }}
              aria-current={isCurrent ? 'step' : undefined}
              {...(isDisabled && { 'aria-disabled': 'true' })}
              aria-label={isSkipped ? `${step.name} (Skipped)` : step.name}
              title={isSkipped ? `${step.name} (Skipped)` : step.name}
            >
              <div className={numberClass}>
                {step.completed ? (
                  <Check size={18} />
                ) : step.hasErrors ? (
                  <AlertCircle size={18} />
                ) : isSkipped ? (
                  <span className="skipped-indicator">-</span>
                ) : (
                  index + 1
                )}
              </div>
              <span className="wizard-step-name">{step.name}</span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`wizard-step-connector ${
                  step.completed ? 'completed' : ''
                } ${isSkipped ? 'skipped' : ''}`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default WizardStepIndicator;
